import React, { useContext, useState, useMemo, useEffect } from "react";
import { WorkspaceContext } from "../context/WorkspaceContext";

import { API_BASE } from "../config/apiConfig";
const PAGE_SIZE = 15;

export function formatRunId(runId) {
  if (!runId) return "";
  const match = runId.match(/^RUN-(\d{4})(\d{2})(\d{2})-(\d{4})/i);
  return match ? `RUN-${match[1]}/${match[2]}/${match[3]}-${match[4]}` : runId;
}

export function getFriendlyReportId(run) {
  if (!run) return "";
  const project = (run.project === "Rabbit" ? "Qintra" : run.project) || "Qintra";
  let uniqueNum = "0000";
  if (run.run_id) {
    const stdMatch = run.run_id.match(/RUN-\d{8}-(\d{4})/i);
    if (stdMatch) {
      uniqueNum = stdMatch[1];
    } else {
      const matches = run.run_id.match(/(\d+)(?:\D*)$/);
      if (matches) {
        uniqueNum = matches[1].padStart(4, "0").slice(-4);
      }
    }
  }
  return `${project}_${uniqueNum}`;
}

export function formatGeneratedDate(timestamp) {
  if (!timestamp) return "N/A";
  const dateMatch = timestamp.match(/(\d{4})(\d{2})(\d{2})/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    const y = parseInt(year), m = parseInt(month), d = parseInt(day);
    if (y >= 2020 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${year}-${month}-${day}`;
    }
  }
  const parsedDate = new Date(timestamp);
  if (!isNaN(parsedDate.getTime()) && timestamp.length > 8) {
    return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${String(parsedDate.getDate()).padStart(2, "0")}`;
  }
  return "N/A";
}

function extractBrowser(runId) {
  if (!runId) return "chromium";
  const lower = runId.toLowerCase();
  for (const b of ["firefox", "webkit", "safari", "edge", "chrome", "chromium"]) {
    if (lower.includes(b)) return b;
  }
  return "chromium";
}

const EXECUTION_TYPES = ["all", "regression", "smoke", "sanity", "functional", "performance", "api", "ui"];
const STATUS_OPTIONS = ["passed", "failed", "running", "queued", "cancelled"];
const BROWSER_OPTIONS = ["chromium", "chrome", "firefox", "edge", "webkit", "safari"];

const EMPTY_FILTERS = {
  reportId: "",
  dateFrom: "",
  dateTo: "",
  runType: "all",
  status: [],
  browser: [],
  generatedBy: "",
  project: "all",
  durationMin: "",
  durationMax: "",
};

function MultiSelectChips({ options, selected, onChange, colorMap }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      {options.map(opt => {
        const active = selected.includes(opt);
        const color = (colorMap && colorMap[opt]) || { bg: "#f1f5f9", border: "#cbd5e1", text: "#475569", activeBg: "#6366f1", activeBorder: "#6366f1", activeText: "#ffffff" };
        return (
          <button
            key={opt}
            onClick={() => onChange(active ? selected.filter(s => s !== opt) : [...selected, opt])}
            style={{
              padding: "4px 10px", borderRadius: "20px",
              border: "1.5px solid " + (active ? color.activeBorder : color.border),
              background: active ? color.activeBg : color.bg,
              color: active ? color.activeText : color.text,
              fontSize: "11px", fontWeight: "600", cursor: "pointer",
              transition: "all 0.15s", textTransform: "capitalize",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function FilterPanel({ open, onClose, filters, onApply, users, projects }) {
  const [draft, setDraft] = useState(filters);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setDraft(filters); }, [open]);

  if (!open) return null;

  const set = (key, val) => setDraft(prev => ({ ...prev, [key]: val }));

  const statusColors = {
    passed: { bg: "#ecfdf5", border: "#86efac", text: "#15803d", activeBg: "#16a34a", activeBorder: "#15803d", activeText: "#fff" },
    failed: { bg: "#fef2f2", border: "#fca5a5", text: "#b91c1c", activeBg: "#ef4444", activeBorder: "#b91c1c", activeText: "#fff" },
    running: { bg: "#eff6ff", border: "#93c5fd", text: "#FD6101", activeBg: "#3b82f6", activeBorder: "#FD6101", activeText: "#fff" },
    queued: { bg: "#fefce8", border: "#fde047", text: "#ca8a04", activeBg: "#eab308", activeBorder: "#ca8a04", activeText: "#fff" },
    cancelled: { bg: "#f8fafc", border: "#cbd5e1", text: "#64748b", activeBg: "#64748b", activeBorder: "#475569", activeText: "#fff" },
  };

  const browserColors = {
    chromium: { bg: "#fff7ed", border: "#fdba74", text: "#c2410c", activeBg: "#f97316", activeBorder: "#ea580c", activeText: "#fff" },
    chrome: { bg: "#fef9c3", border: "#fde047", text: "#92400e", activeBg: "#eab308", activeBorder: "#ca8a04", activeText: "#fff" },
    firefox: { bg: "#fdf4ff", border: "#e879f9", text: "#86198f", activeBg: "#d946ef", activeBorder: "#a21caf", activeText: "#fff" },
    edge: { bg: "#eff6ff", border: "#60a5fa", text: "#1e40af", activeBg: "#FD6101", activeBorder: "#FD6101", activeText: "#fff" },
    webkit: { bg: "#f0fdf4", border: "#4ade80", text: "#166534", activeBg: "#22c55e", activeBorder: "#15803d", activeText: "#fff" },
    safari: { bg: "#f0f9ff", border: "#38bdf8", text: "#0369a1", activeBg: "#0ea5e9", activeBorder: "#0284c7", activeText: "#fff" },
  };

  return (
    <div>
      <style>{`
        @keyframes fpSlideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .fp-lbl { font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:6px; display:block; }
        .fp-inp { width:100%; padding:8px 10px; border:1.5px solid #e2e8f0; border-radius:8px; font-size:13px; color:#0f172a; background:#f8fafc; outline:none; box-sizing:border-box; }
        .fp-inp:focus { border-color:#6366f1; background:#fff; }
        .fp-sel { width:100%; padding:8px 10px; border:1.5px solid #e2e8f0; border-radius:8px; font-size:13px; color:#0f172a; background:#f8fafc; outline:none; cursor:pointer; }
        .fp-sec { margin-bottom:18px; }
      `}</style>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 1200, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: "420px", background: "#fff", zIndex: 1300, boxShadow: "-4px 0 40px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column", animation: "fpSlideIn 0.25s ease", overflow: "hidden" }}>
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#002247" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="#94a3b8" strokeWidth="2.5" fill="none"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            <span style={{ fontWeight: "700", fontSize: "15px", color: "#fff" }}>Filter Reports</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: "4px" }}>
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2.5" fill="none"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          <div className="fp-sec">
            <label className="fp-lbl">Report ID</label>
            <input className="fp-inp" placeholder="Search by Run ID (partial)..." value={draft.reportId} onChange={e => set("reportId", e.target.value)} />
          </div>
          <div className="fp-sec">
            <label className="fp-lbl">Generated Date Range</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div>
                <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>From</div>
                <input type="date" className="fp-inp" value={draft.dateFrom} onChange={e => set("dateFrom", e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>To</div>
                <input type="date" className="fp-inp" value={draft.dateTo} onChange={e => set("dateTo", e.target.value)} />
              </div>
            </div>
          </div>
          <div className="fp-sec">
            <label className="fp-lbl">Execution Type</label>
            <select className="fp-sel" value={draft.runType} onChange={e => set("runType", e.target.value)}>
              {EXECUTION_TYPES.map(t => <option key={t} value={t}>{t === "all" ? "All Types" : t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
          <div className="fp-sec">
            <label className="fp-lbl">Status {draft.status.length > 0 && <span style={{ color: "#FD6101", fontWeight: "800" }}>({draft.status.length})</span>}</label>
            <MultiSelectChips options={STATUS_OPTIONS} selected={draft.status} onChange={val => set("status", val)} colorMap={statusColors} />
          </div>
          <div className="fp-sec">
            <label className="fp-lbl">Browser {draft.browser.length > 0 && <span style={{ color: "#FD6101", fontWeight: "800" }}>({draft.browser.length})</span>}</label>
            <MultiSelectChips options={BROWSER_OPTIONS} selected={draft.browser} onChange={val => set("browser", val)} colorMap={browserColors} />
          </div>
          <div className="fp-sec">
            <label className="fp-lbl">Generated By</label>
            <input className="fp-inp" list="rpt-users-dl" placeholder="Search by email or name..." value={draft.generatedBy} onChange={e => set("generatedBy", e.target.value)} />
            <datalist id="rpt-users-dl">{users.map(u => <option key={u} value={u} />)}</datalist>
          </div>
          <div className="fp-sec">
            <label className="fp-lbl">Project</label>
            <select className="fp-sel" value={draft.project} onChange={e => set("project", e.target.value)}>
              <option value="all">All Projects</option>
              {projects.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="fp-sec">
            <label className="fp-lbl">Execution Duration (seconds)</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div>
                <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>Min (s)</div>
                <input type="number" min="0" className="fp-inp" placeholder="0" value={draft.durationMin} onChange={e => set("durationMin", e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>Max (s)</div>
                <input type="number" min="0" className="fp-inp" placeholder="no limit" value={draft.durationMax} onChange={e => set("durationMax", e.target.value)} />
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "16px 20px", borderTop: "1px solid #f1f5f9", display: "flex", gap: "8px", background: "#fafafa" }}>
          <button onClick={() => { onApply(draft); onClose(); }}
            style={{ flex: 2, padding: "10px 0", background: "#FD6101", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "700", fontSize: "13px", cursor: "pointer", boxShadow: "0 4px 6px -1px rgba(253,97,1,0.3)" }}
            onMouseEnter={e => e.currentTarget.style.background = "#e05300"}
            onMouseLeave={e => e.currentTarget.style.background = "#FD6101"}>
            Apply Filters
          </button>
          <button onClick={() => setDraft(EMPTY_FILTERS)}
            style={{ flex: 1, padding: "10px 0", background: "#fff", color: "#64748b", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontWeight: "600", fontSize: "13px", cursor: "pointer" }}>
            Reset
          </button>
          <button onClick={onClose}
            style={{ flex: 1, padding: "10px 0", background: "#fff", color: "#64748b", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontWeight: "600", fontSize: "13px", cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ActiveFilterChips({ filters, onRemove, onClearAll }) {
  const chips = [];
  if (filters.reportId) chips.push({ key: "reportId", label: "ID: " + filters.reportId, clear: () => onRemove({ ...filters, reportId: "" }) });
  if (filters.dateFrom && filters.dateTo) chips.push({ key: "dateRange", label: "Date: " + filters.dateFrom + " to " + filters.dateTo, clear: () => onRemove({ ...filters, dateFrom: "", dateTo: "" }) });
  else if (filters.dateFrom) chips.push({ key: "dateFrom", label: "From: " + filters.dateFrom, clear: () => onRemove({ ...filters, dateFrom: "" }) });
  else if (filters.dateTo) chips.push({ key: "dateTo", label: "To: " + filters.dateTo, clear: () => onRemove({ ...filters, dateTo: "" }) });
  if (filters.runType && filters.runType !== "all") chips.push({ key: "runType", label: "Type: " + filters.runType, clear: () => onRemove({ ...filters, runType: "all" }) });
  filters.status.forEach(s => chips.push({ key: "status_" + s, label: "Status: " + s, clear: () => onRemove({ ...filters, status: filters.status.filter(x => x !== s) }) }));
  filters.browser.forEach(b => chips.push({ key: "browser_" + b, label: "Browser: " + b, clear: () => onRemove({ ...filters, browser: filters.browser.filter(x => x !== b) }) }));
  if (filters.generatedBy) chips.push({ key: "generatedBy", label: "By: " + filters.generatedBy, clear: () => onRemove({ ...filters, generatedBy: "" }) });
  if (filters.project && filters.project !== "all") chips.push({ key: "project", label: "Project: " + filters.project, clear: () => onRemove({ ...filters, project: "all" }) });
  if (filters.durationMin) chips.push({ key: "durationMin", label: "Min: " + filters.durationMin + "s", clear: () => onRemove({ ...filters, durationMin: "" }) });
  if (filters.durationMax) chips.push({ key: "durationMax", label: "Max: " + filters.durationMax + "s", clear: () => onRemove({ ...filters, durationMax: "" }) });
  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", padding: "10px 0", marginBottom: "4px" }}>
      <span style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginRight: "4px" }}>Active Filters:</span>
      {chips.map(chip => (
        <span key={chip.key} style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 8px 3px 10px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "20px", fontSize: "11.5px", fontWeight: "600", color: "#c2410c" }}>
          {chip.label}
          <button onClick={chip.clear} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#FD6101", display: "flex", lineHeight: 1 }}>
            <svg viewBox="0 0 24 24" width="11" height="11" stroke="currentColor" strokeWidth="3" fill="none"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </span>
      ))}
      <button onClick={onClearAll} style={{ padding: "3px 10px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", fontSize: "11px", fontWeight: "600", color: "#ef4444", cursor: "pointer" }}>Clear All</button>
    </div>
  );
}

export default function Reports() {
  const {
    activeRepoProject, currentUser, downloadLog, playRunVideo,
    reportsList, sortedReportsList, setReportModalUrl,
    setRunProject, setRunTestCase, setRunTriggeredBy,
    setShowReportModal, setShowStartRunModal, testCaseRepo,
    setReportModalMeta, setReportSteps, setReportView
  } = useContext(WorkspaceContext);

  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("generatedDate");
  const [sortDirection, setSortDirection] = useState("desc");
  console.log("=== REPORTS RENDER ===", { showFilterPanel, appliedFilters, sortField, sortDirection });


  const projects = useMemo(() => {
    const fromReports = [...new Set((reportsList || []).map(r => r.project).filter(Boolean))];
    const fromRepo = testCaseRepo ? Object.keys(testCaseRepo) : [];
    return [...new Set([...fromReports, ...fromRepo])].sort();
  }, [reportsList, testCaseRepo]);

  const users = useMemo(() => [...new Set((reportsList || []).map(r => r.triggered_by).filter(Boolean))].sort(), [reportsList]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (appliedFilters.reportId) c++;
    if (appliedFilters.dateFrom) c++;
    if (appliedFilters.dateTo) c++;
    if (appliedFilters.runType && appliedFilters.runType !== "all") c++;
    c += appliedFilters.status.length;
    c += appliedFilters.browser.length;
    if (appliedFilters.generatedBy) c++;
    if (appliedFilters.project && appliedFilters.project !== "all") c++;
    if (appliedFilters.durationMin) c++;
    if (appliedFilters.durationMax) c++;
    return c;
  }, [appliedFilters]);

  const filteredList = useMemo(() => {
    let list = [...(sortedReportsList || [])];
    const f = appliedFilters;
    if (f.reportId) { const t = f.reportId.toLowerCase(); list = list.filter(r => (r.run_id || "").toLowerCase().includes(t)); }
    if (f.dateFrom) { const from = f.dateFrom.replace(/-/g, ""); list = list.filter(r => { const d = (r.run_id || "").replace(/^RUN-/, "").substring(0, 8); return d >= from; }); }
    if (f.dateTo) { const to = f.dateTo.replace(/-/g, ""); list = list.filter(r => { const d = (r.run_id || "").replace(/^RUN-/, "").substring(0, 8); return d <= to; }); }
    if (f.runType && f.runType !== "all") { list = list.filter(r => (r.type || "").toLowerCase() === f.runType.toLowerCase()); }
    if (f.status.length > 0) { list = list.filter(r => f.status.map(s => s.toLowerCase()).includes((r.status || "").toLowerCase())); }
    if (f.browser.length > 0) { list = list.filter(r => { const b = extractBrowser(r.run_id); return f.browser.map(x => x.toLowerCase()).includes(b.toLowerCase()); }); }
    if (f.generatedBy) { const t = f.generatedBy.toLowerCase(); list = list.filter(r => (r.triggered_by || "").toLowerCase().includes(t)); }
    if (f.project && f.project !== "all") { list = list.filter(r => (r.project || "").toLowerCase() === f.project.toLowerCase()); }
    if (f.durationMin !== "" && f.durationMin !== undefined && f.durationMin !== null) { const min = parseInt(f.durationMin); if (!isNaN(min)) list = list.filter(r => (r.duration || 0) >= min); }
    if (f.durationMax !== "" && f.durationMax !== undefined && f.durationMax !== null) { const max = parseInt(f.durationMax); if (!isNaN(max)) list = list.filter(r => (r.duration || 0) <= max); }
    return list;
  }, [sortedReportsList, appliedFilters]);

  const sortedFilteredList = useMemo(() => {
    const list = [...filteredList];
    if (!sortField) return list;

    list.sort((a, b) => {
      let valA = "";
      let valB = "";

      if (sortField === "reportId") {
        valA = getFriendlyReportId(a);
        valB = getFriendlyReportId(b);
      } else if (sortField === "generatedDate") {
        valA = a.timestamp || "";
        valB = b.timestamp || "";
      } else if (sortField === "type") {
        valA = a.type || "";
        valB = b.type || "";
      } else if (sortField === "project") {
        valA = a.project || "";
        valB = b.project || "";
      } else if (sortField === "browser") {
        valA = extractBrowser(a.run_id);
        valB = extractBrowser(b.run_id);
      } else if (sortField === "duration") {
        valA = a.duration || 0;
        valB = b.duration || 0;
      } else if (sortField === "status") {
        valA = a.status || "";
        valB = b.status || "";
      }

      if (typeof valA === "string") {
        return sortDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else {
        return sortDirection === "asc"
          ? (valA > valB ? 1 : -1)
          : (valB > valA ? 1 : -1);
      }
    });

    return list;
  }, [filteredList, sortField, sortDirection]);

  useEffect(() => { setCurrentPage(1); }, [appliedFilters]);

  const totalPages = Math.max(1, Math.ceil(sortedFilteredList.length / PAGE_SIZE));
  const pagedList = sortedFilteredList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const handleClearAll = () => setAppliedFilters(EMPTY_FILTERS);
  const filterBtnActive = activeFilterCount > 0;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const renderSortableHeader = (label, field) => {
    const isSorted = sortField === field;
    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: "pointer", userSelect: "none", transition: "all 0.2s" }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span>{label}</span>
          <span style={{ fontSize: "10px", color: isSorted ? "#6366f1" : "#94a3b8", display: "inline-block", transition: "transform 0.2s" }}>
            {isSorted ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
          </span>
        </div>
      </th>
    );
  };

  return (
    <div className="table-card">

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", marginBottom: "24px" }}>
        <div style={{ background: "#002247", borderRadius: "12px", padding: "20px", color: "#fff", boxShadow: "0 4px 6px -1px rgb(0 0 0/0.1)", textAlign: "left" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#94a3b8", letterSpacing: "0.5px" }}>Total Test Runs</div>
          <div style={{ fontSize: "32px", fontWeight: "800", marginTop: "8px" }}>{(reportsList || []).length}</div>
        </div>
        <div style={{ background: "#059669", borderRadius: "12px", padding: "20px", color: "#fff", boxShadow: "0 4px 6px -1px rgb(0 0 0/0.1)", textAlign: "left" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#a7f3d0", letterSpacing: "0.5px" }}>Passed Executions</div>
          <div style={{ fontSize: "32px", fontWeight: "800", marginTop: "8px" }}>{(reportsList || []).filter(r => r.status === "passed").length}</div>
        </div>
        <div style={{ background: "#dc2626", borderRadius: "12px", padding: "20px", color: "#fff", boxShadow: "0 4px 6px -1px rgb(0 0 0/0.1)", textAlign: "left" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#fca5a5", letterSpacing: "0.5px" }}>Failed Executions</div>
          <div style={{ fontSize: "32px", fontWeight: "800", marginTop: "8px" }}>{(reportsList || []).filter(r => r.status !== "passed").length}</div>
        </div>
      </div>

      <div className="panel-header-row" style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: "12px", marginBottom: "4px" }}>
        <div style={{ textAlign: "left" }}>
          <h3 className="panel-header-title" style={{ marginBottom: 0 }}>Stored Test Suite Reports</h3>
          <p style={{ color: "var(--text-muted)", fontSize: "13px", marginTop: "4px" }}>
            {activeFilterCount > 0 ? ("Showing " + filteredList.length + " of " + (reportsList || []).length + " reports") : "Download HTML and PDF test execution files."}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button
            onClick={() => setShowFilterPanel(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 14px", height: "38px", background: filterBtnActive ? "#fff7ed" : "#f8fafc", border: filterBtnActive ? "1.5px solid #FD6101" : "1.5px solid #e2e8f0", borderRadius: "8px", color: filterBtnActive ? "#FD6101" : "#475569", fontWeight: "600", fontSize: "13px", cursor: "pointer", transition: "all 0.2s" }}>
            <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            Filter
            {activeFilterCount > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", borderRadius: "50%", background: "#FD6101", color: "#fff", fontSize: "10px", fontWeight: "800" }}>{activeFilterCount}</span>
            )}
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              const defaultProj = (testCaseRepo && Object.keys(testCaseRepo).length > 0) ? Object.keys(testCaseRepo)[0] : "Qintra";
              setRunProject((activeRepoProject === "Rabbit" ? "Qintra" : activeRepoProject) || defaultProj);
              const firstCase = (testCaseRepo && testCaseRepo[activeRepoProject || defaultProj] && testCaseRepo[activeRepoProject || defaultProj][0]) ? testCaseRepo[activeRepoProject || defaultProj][0].filename : "";
              setRunTestCase(firstCase);
              setRunTriggeredBy(currentUser && currentUser.email ? currentUser.email : "");
              setShowStartRunModal(true);
            }}
            style={{ margin: 0, padding: "10px 18px", height: "40px", background: "var(--primary)" }}>
            Launch Test Run
          </button>
        </div>
      </div>

      <ActiveFilterChips filters={appliedFilters} onRemove={setAppliedFilters} onClearAll={handleClearAll} />

      <div style={{ marginTop: "8px" }}>
        <table className="projects-table">
          <thead>
            <tr>
              {renderSortableHeader("Report ID", "reportId")}
              {renderSortableHeader("Generated Date", "generatedDate")}
              {renderSortableHeader("Type", "type")}
              {renderSortableHeader("Project", "project")}
              {renderSortableHeader("Browser", "browser")}
              {renderSortableHeader("Duration", "duration")}
              {renderSortableHeader("Status", "status")}
              <th>Downloads</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
                    <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg viewBox="0 0 24 24" width="24" height="24" stroke="#94a3b8" strokeWidth="2" fill="none"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: "15px", fontWeight: "700", color: "#0f172a", marginBottom: "4px" }}>
                        {activeFilterCount > 0 ? "No reports found matching the selected filters." : "No reports stored yet."}
                      </div>
                      <div style={{ fontSize: "13px", color: "#64748b" }}>
                        {activeFilterCount > 0 ? "Try adjusting or removing some filters to see more results." : "Run a test to generate reports."}
                      </div>
                    </div>
                    {activeFilterCount > 0 && (
                      <button onClick={handleClearAll} style={{ padding: "8px 18px", background: "#FD6101", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#e05300"}
                        onMouseLeave={e => e.currentTarget.style.background = "#FD6101"}>
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              pagedList.map((run, index) => {
                const isPassed = run.status === "passed";
                const browser = extractBrowser(run.run_id);
                return (
                  <tr key={run.run_id || index}>
                    <td style={{ fontWeight: "600", fontFamily: "monospace", fontSize: "12px" }}>{getFriendlyReportId(run)}</td>
                    <td>{formatGeneratedDate(run.timestamp)}</td>
                    <td style={{ textTransform: "capitalize" }}>{run.type}</td>
                    <td><span style={{ display: "inline-block", padding: "2px 8px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "12px", fontSize: "11px", fontWeight: "600", color: "#0369a1" }}>{run.project || "N/A"}</span></td>
                    <td><span style={{ display: "inline-block", padding: "2px 8px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", fontSize: "11px", fontWeight: "600", color: "#475569", textTransform: "capitalize" }}>{browser}</span></td>
                    <td style={{ fontFamily: "monospace", fontSize: "12px" }}>{run.duration ? run.duration + "s" : "N/A"}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "20px", fontWeight: "700", fontSize: "12px", background: isPassed ? "#ecfdf5" : "#fef2f2", color: isPassed ? "#15803d" : "#b91c1c", border: "1px solid " + (isPassed ? "#86efac" : "#fca5a5") }}>
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: isPassed ? "#16a34a" : "#ef4444" }} />
                        {isPassed ? "Passed" : "Failed"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button
                          onClick={e => {
                            e.preventDefault();
                            setReportModalUrl(API_BASE + "/reports/" + run.type + "/run_" + run.run_id + "/report.html");
                            setReportModalMeta(run);
                            setReportSteps(null);
                            setReportView('steps');
                            fetch(API_BASE + "/run-steps/" + (run.type || 'regression') + "/" + run.run_id)
                              .then(res => res.json())
                              .then(d => setReportSteps(d))
                              .catch(() => setReportSteps({ steps: [], error: 'Could not load steps' }));
                            setShowReportModal(true);
                          }}
                          style={{ background: "rgba(165,148,249,0.08)", border: "1px solid rgba(165,148,249,0.2)", borderRadius: "6px", color: "#A594F9", fontWeight: "700", cursor: "pointer", padding: "5px 10px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "4px", transition: "all 0.2s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(165,148,249,0.15)"}
                          onMouseLeave={e => e.currentTarget.style.background = "rgba(165,148,249,0.08)"}>
                          HTML
                        </button>
                        <button
                          onClick={() => playRunVideo(run.run_id, run.type)}
                          style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: "6px", color: "#3b82f6", fontWeight: "700", cursor: "pointer", padding: "5px 10px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "4px", transition: "all 0.2s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(59,130,246,0.15)"}
                          onMouseLeave={e => e.currentTarget.style.background = "rgba(59,130,246,0.08)"}>
                          Video
                        </button>
                        <button
                          onClick={() => window.open(API_BASE + "/reports/" + run.type + "/run_" + run.run_id + "/report.html?print=1", "_blank")}
                          style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: "6px", color: "#dc2626", fontWeight: "700", cursor: "pointer", padding: "5px 10px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "4px", transition: "all 0.2s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(220,38,38,0.15)"}
                          onMouseLeave={e => e.currentTarget.style.background = "rgba(220,38,38,0.08)"}>
                          PDF
                        </button>
                        <button
                          onClick={() => downloadLog(run.run_id, run.type)}
                          style={{ background: "rgba(71,85,105,0.08)", border: "1px solid rgba(71,85,105,0.2)", borderRadius: "6px", color: "#475569", fontWeight: "700", cursor: "pointer", padding: "5px 10px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "4px", transition: "all 0.2s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(71,85,105,0.15)"}
                          onMouseLeave={e => e.currentTarget.style.background = "rgba(71,85,105,0.08)"}>
                          Log
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {filteredList.length > 0 && totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "16px", padding: "12px 0", borderTop: "1px solid #f1f5f9" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredList.length)}&ndash;{Math.min(currentPage * PAGE_SIZE, filteredList.length)} of <strong>{filteredList.length}</strong> reports
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              style={{ padding: "6px 12px", borderRadius: "6px", border: "1.5px solid #e2e8f0", background: currentPage === 1 ? "#f8fafc" : "#fff", color: currentPage === 1 ? "#cbd5e1" : "#374151", fontWeight: "600", fontSize: "12px", cursor: currentPage === 1 ? "default" : "pointer" }}>
              Prev
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pg = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button key={pg} onClick={() => setCurrentPage(pg)}
                  style={{ width: "34px", height: "32px", borderRadius: "6px", border: "1.5px solid " + (pg === currentPage ? "#FD6101" : "#e2e8f0"), background: pg === currentPage ? "#FD6101" : "#fff", color: pg === currentPage ? "#fff" : "#374151", fontWeight: "700", fontSize: "12px", cursor: "pointer" }}
                  onMouseEnter={e => { if (pg !== currentPage) e.currentTarget.style.borderColor = "#FD6101"; }}
                  onMouseLeave={e => { if (pg !== currentPage) e.currentTarget.style.borderColor = "#e2e8f0"; }}>
                  {pg}
                </button>
              );
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              style={{ padding: "6px 12px", borderRadius: "6px", border: "1.5px solid #e2e8f0", background: currentPage === totalPages ? "#f8fafc" : "#fff", color: currentPage === totalPages ? "#cbd5e1" : "#374151", fontWeight: "600", fontSize: "12px", cursor: currentPage === totalPages ? "default" : "pointer" }}>
              Next
            </button>
          </div>
        </div>
      )}

      {activeFilterCount > 0 && filteredList.length > 0 && (
        <div style={{ marginTop: "8px", padding: "8px 14px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", fontSize: "12.5px", color: "#c2410c", fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <svg viewBox="0 0 24 24" width="13" height="13" stroke="#FD6101" strokeWidth="2.5" fill="none"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          {filteredList.length} report{filteredList.length !== 1 ? "s" : ""} match{filteredList.length === 1 ? "es" : ""} your filters
          {" — "}
          <button onClick={handleClearAll} style={{ background: "none", border: "none", color: "#FD6101", cursor: "pointer", fontWeight: "700", padding: 0, textDecoration: "underline", fontSize: "12.5px" }}>Clear All</button>
        </div>
      )}

      <FilterPanel
        open={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={appliedFilters}
        onApply={f => setAppliedFilters(f)}
        users={users}
        projects={projects}
      />

    </div>
  );
}

